import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import CapturesTab from "../../components/ManagementUnits/Tabs/CapturesTab";
import FilesTab from "../../components/ManagementUnits/Tabs/FilesTab";
import GeneralTab from "../../components/ManagementUnits/Tabs/GeneralTab";
import ProtectionZoneTab from "../../components/ManagementUnits/Tabs/ProtectionZoneTab";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useManagementUnit } from "../../hooks/ManagementUnit/useManagementUnit";
import { setTab } from "../../store/UI";
import { UIStore } from "../../types/store";
import { usePermissions } from "../../hooks";

interface FilterOptions {
  entityId?: number;
  managementUnitId?: number;
  militaryMap?: string;
  search?: string;
}

export default function DetailsManagementUnit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const { managementUnitId } = useParams<{ managementUnitId: string }>();
  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [activeTab, setActiveTab] = useState(tabState[location.pathname] || "general");

  const { managementUnitData, update, isPending, isUpdating, isError, error, refetch } = useManagementUnit(managementUnitId);

  const tabs = [
    { 
      id: "general",
      label: t("management-units.tabs.general")
    },
    { 
      id: "captures",
      label: t("management-units.tabs.captures"),
      permission: "MU_TAB_CAPTURES"
    },
    { 
      id: "protection-zone",
      label: t("management-units.tabs.protection-zone"),
      permission: "MU_TAB_PROTECTION_ZONE"
    },
    { 
      id: "files",
      label: t("management-units.tabs.files"),
      permission: "MU_TAB_FILES"
    },
  ].filter(tab => hasPermission(tab.permission));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  return (
    <Paper elevation={3} sx={{ borderRadius: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100% " }} variant="h4">
          {t("management-units.label")}
          {managementUnitData ? ` - ${managementUnitData.name}` : ""}
        </Typography>

        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map(tab => (
          <Tab key={tab.id} value={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={{ p: 2, display: "grid", gridTemplateColumns: "1fr" }}>
        <Fade in={activeTab === "general"} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
          <Box sx={{ gridColumn: 1, gridRow: 1 }}>
            <GeneralTab
              managementUnitData={managementUnitData}
              onUpdate={update}
              isPending={isPending}
              isUpdating={isUpdating}
              isError={isError}
              error={error}
              refetch={refetch}
            />
          </Box>
        </Fade>

        {hasPermission("MU_TAB_CAPTURES") && (
          <Fade in={activeTab === "captures"} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
            <Box sx={{ gridColumn: 1, gridRow: 1 }}>
              <CapturesTab
                managementUnitId={managementUnitId}
                entityId={managementUnitData?.entityId}
              />
            </Box>
          </Fade>
        )}

        {hasPermission("MU_TAB_PROTECTION_ZONE") && (
          <Fade in={activeTab === "protection-zone"} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
            <Box sx={{ gridColumn: 1, gridRow: 1 }}>
              <ProtectionZoneTab managementUnitId={managementUnitId} handleError={handleError} />
            </Box>
          </Fade>
        )}

        {hasPermission("MU_TAB_FILES") && (
          <Fade in={activeTab === "files"} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
            <Box sx={{ gridColumn: 1, gridRow: 1 }}>
              {managementUnitData && (
                <FilesTab
                  managementUnitId={Number.parseInt(managementUnitId!)}
                  entityId={managementUnitData.entityId}
                />
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Paper>
  );
}
